(ns client-portal.client.oasys.sessions.filters.data
  (:require [client-portal.client.concepts.data :as concepts.d]
            [client-portal.client.oasys.sessions.data :as oasys.sessions.d]
            [client-portal.client.products.data :as products.d]
            [client-portal.client.voice-insights.data :as vi.d]
            [client-portal.client.voice-insights.sessions.data :as vi.sessions.d]
            [shared.core.reactive :as re]
            [shared.core.router :as router]
            [shared.core.util :as util]))

(def filter-data-path (conj oasys.sessions.d/root-path :filter-data))
(def popover-open?-path (conj oasys.sessions.d/root-path :filter-popover-open?))
(def topics-by-concept-id-path (conj oasys.sessions.d/root-path :topics))
(def channels-by-concept-id-path (conj oasys.sessions.d/root-path :channels))
(def metrics-by-concept-id-path (conj oasys.sessions.d/root-path :metrics))
(def intents-by-concept-id-path (conj oasys.sessions.d/root-path :intents))

(def default-filter-pair
  {:by "topics"
   :condition "include"
   :join "or"
   :value nil
   :children []})

;; A topic filter's children narrow the matched topic, as child tags do in Insights.
(def default-topic-child
  {:by "resolution-status"
   :condition "include"
   :join "or"
   :value nil})

(def topic-child-options
  [{:value "resolution-status" :text "Resolution"}])

(def resolution-statuses ["RESOLVED" "PARTIALLY_RESOLVED" "UNRESOLVED" "MISUNDERSTOOD"])

;; Day of week and time of day are read off the UTC conversation timestamp — OASYS
;; conversations carry no local time.
(def filter-by-options
  [{:value "topics" :text "Topics"}
   {:value "intents" :text "Intents"}
   {:value "resolution" :text "Session Resolution"}
   {:value "escalation" :text "Escalation"}
   {:value "channel" :text "Channel"}
   {:value "metrics" :text "Metrics"}
   {:value "day-of-week" :text "Day of Week (UTC)"}
   {:value "time-of-day" :text "Time of Day (UTC)"}
   {:value "duration" :text "Duration"}])

(def any-only-bys #{"resolution" "escalation" "channel" "metrics" "day-of-week" "time-of-day" "duration"})

(defn populate-default-filter-if-empty [filters]
  (if (empty? filters)
    [default-filter-pair]
    filters))

(defn filter-data [db]
  (populate-default-filter-if-empty (get-in db filter-data-path)))

(re/reg-sub :oasys-filters/filter-data- filter-data)

(re/reg-sub
  :oasys-filters/applied-filters-
  (fn [db]
    (vi.d/filter-data-from-route db)))

;; Filters without a value are dropped on apply, so the edited filters can differ from the
;; applied ones and still change nothing. Compared decoded, as key order isn't stable.
(re/reg-sub
  :oasys-filters/unchanged?-
  (fn [db]
    (let [->filters #(some-> % (util/base-64-decode) (util/json-string->map))]
      (= (->filters (vi.sessions.d/filters->query-param (filter-data db)))
         (->filters (:fd (router/current-query-params db)))))))

(re/reg-sub
  :oasys-filters/popover-open?-
  (fn [db]
    (boolean (get-in db popover-open?-path))))

(re/reg-event-db
  :oasys-filters/initialize re/middleware
  (fn [db]
    (assoc-in db filter-data-path (populate-default-filter-if-empty (vi.d/filter-data-from-route db)))))

(re/reg-event-db
  :oasys-filters/add-filter-pair re/middleware
  (fn [db]
    (assoc-in db filter-data-path (conj (vec (filter-data db)) default-filter-pair))))

(re/reg-event-db
  :oasys-filters/delete-filter-pair re/middleware
  (fn [db [index]]
    (assoc-in db filter-data-path (-> (filter-data db)
                                      (vec)
                                      (util/vec-remove index)
                                      (populate-default-filter-if-empty)))))

(re/reg-event-db
  :oasys-filters/update-filter-pair re/middleware
  (fn [db [index k v]]
    (assoc-in db filter-data-path (update (vec (filter-data db)) index assoc k v))))

(re/reg-event-db
  :oasys-filters/select-by re/middleware
  (fn [db [index by]]
    (assoc-in db filter-data-path (update (vec (filter-data db)) index #(-> %
                                                                            (assoc :by by
                                                                                   :value nil
                                                                                   :join "or"
                                                                                   :children [])
                                                                            (dissoc :metric))))))

(defn update-children [db index f]
  (assoc-in db filter-data-path (update (vec (filter-data db)) index update :children #(f (vec %)))))

;; A topic filter always shows at least one child row; until one is edited it is the
;; default, which isn't in the filter yet.
(re/reg-event-db
  :oasys-filters/add-child re/middleware
  (fn [db [index]]
    (update-children db index #(conj (or (not-empty %) [default-topic-child]) default-topic-child))))

(re/reg-event-db
  :oasys-filters/remove-child re/middleware
  (fn [db [index child-idx]]
    (update-children db index #(cond-> % (< child-idx (count %)) (util/vec-remove child-idx)))))

(re/reg-event-db
  :oasys-filters/update-child re/middleware
  (fn [db [index child-idx k v]]
    (update-children db index #(update % child-idx (fn [child] (assoc (merge default-topic-child child) k v))))))

(re/reg-event-db
  :oasys-filters/select-metric re/middleware
  (fn [db [index code]]
    (assoc-in db filter-data-path (update (vec (filter-data db)) index assoc :metric code :value nil))))

(re/reg-event-db
  :oasys-filters/apply re/middleware
  (fn [db]
    (let [new-fd (vi.sessions.d/filters->query-param (filter-data db))]
      (when (not= (:fd (router/current-query-params db)) new-fd)
        (re/dispatch [:router/go-with-merged-query-params {:fd new-fd}])
        (re/dispatch [:oasys/reload-sessions]))
      (assoc-in db popover-open?-path false))))

(re/reg-event-db
  :oasys-filters/remove-applied-filter re/middleware
  (fn [db [index]]
    (re/dispatch [:oasys-filters/apply])
    (assoc-in db filter-data-path (-> (vi.d/filter-data-from-route db)
                                      (vec)
                                      (util/vec-remove index)
                                      (populate-default-filter-if-empty)))))

(re/reg-event-db
  :oasys-filters/clear re/middleware
  (fn [db]
    (re/dispatch [:router/go-with-merged-query-params {:fd nil}])
    (re/dispatch [:oasys/reload-sessions])
    (-> db
        (assoc-in filter-data-path [default-filter-pair])
        (assoc-in popover-open?-path false))))

;; A failed catalog load is kept as `:error` rather than an empty list, so it isn't read as
;; "nothing recorded"; opening the filter clears it, which makes its sub load it again.
(re/reg-event-db
  :oasys-filters/toggle-popover re/middleware
  (fn [db [open?]]
    (let [concept-id (concepts.d/current-concept-id db)
          retry (fn [db path]
                  (cond-> db
                          (= :error (get-in db (conj path concept-id)))
                          (assoc-in (conj path concept-id) nil)))]
      (-> (cond-> db
                  open? (as-> db (reduce retry db [topics-by-concept-id-path
                                                   channels-by-concept-id-path
                                                   metrics-by-concept-id-path
                                                   intents-by-concept-id-path])))
          (assoc-in popover-open?-path (boolean open?))))))

(re/reg-auth0-loader
  :oasys-filters/load-topics
  (fn [[concept-id]]
    (conj topics-by-concept-id-path concept-id))
  (fn [db concept-id]
    {:method :get
     :url "/api/oasys/topics"
     :query-params (merge {:concept-id concept-id}
                          (products.d/project-params db ["oasys"]))})
  :transf #(or (some-> % :body (util/->kebab-case-keywords)) [])
  :error-transf (fn [] :error)
  :quiet-fail? true)

(re/reg-sub
  :oasys-filters/topics-
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          v (get-in db (conj topics-by-concept-id-path concept-id))]
      (if (and concept-id (nil? v))
        (re/dispatch-load [:oasys-filters/load-topics concept-id])
        v))))

(re/reg-auth0-loader
  :oasys-filters/load-channels
  (fn [[concept-id]]
    (conj channels-by-concept-id-path concept-id))
  (fn [db concept-id]
    {:method :get
     :url "/api/oasys/channels"
     :query-params (merge {:concept-id concept-id}
                          (products.d/project-params db ["oasys"]))})
  :transf #(or (some-> % :body (util/->kebab-case-keywords)) [])
  :error-transf (fn [] :error)
  :quiet-fail? true)

(re/reg-sub
  :oasys-filters/channels-
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          v (get-in db (conj channels-by-concept-id-path concept-id))]
      (if (and concept-id (nil? v))
        (re/dispatch-load [:oasys-filters/load-channels concept-id])
        v))))

(re/reg-auth0-loader
  :oasys-filters/load-intents
  (fn [[concept-id]]
    (conj intents-by-concept-id-path concept-id))
  (fn [db concept-id]
    {:method :get
     :url "/api/oasys/intents"
     :query-params (merge {:concept-id concept-id}
                          (products.d/project-params db ["oasys"]))})
  :transf #(or (some-> % :body (util/->kebab-case-keywords)) [])
  :error-transf (fn [] :error)
  :quiet-fail? true)

(re/reg-sub
  :oasys-filters/intents-
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          v (get-in db (conj intents-by-concept-id-path concept-id))]
      (if (and concept-id (nil? v))
        (re/dispatch-load [:oasys-filters/load-intents concept-id])
        v))))

;; The filterable metrics are the custom metrics recording a small set of plain values,
;; each with those values — read from the same cached catalog as the report builder
;; guide and the data-question prompt.
(re/reg-auth0-loader
  :oasys-filters/load-metrics
  (fn [[concept-id]]
    (conj metrics-by-concept-id-path concept-id))
  (fn [db concept-id]
    {:method :get
     :url "/api/oasys/custom-metrics"
     :query-params (merge {:concept-id concept-id}
                          (products.d/project-params db ["oasys"]))})
  :transf #(or (some-> % :body (util/->kebab-case-keywords)) [])
  :error-transf (fn [] :error)
  :quiet-fail? true)

(re/reg-sub
  :oasys-filters/metrics-
  (fn [db]
    (let [concept-id (concepts.d/current-concept-id db)
          v (get-in db (conj metrics-by-concept-id-path concept-id))]
      (if (and concept-id (nil? v))
        (re/dispatch-load [:oasys-filters/load-metrics concept-id])
        v))))
